/**
 * 基于原生 CSS 的自定义滚动条
 * 默认垂直滚动
 * 当前只支持垂直滚动
 */
if (!customElements.get('custom-scrollbar')) {
  customElements.define('custom-scrollbar',
    class CustomScrollBar extends HTMLElement {
      constructor() {
        super();

        this._onScroll = this.onScroll.bind(this);
        this._updateThumb = this.updateThumb.bind(this);
        this._onResize = this.onResize.bind(this);
        this._startDrag = this.startDrag.bind(this);
        this._onDragMove = this.onDragMove.bind(this);
        this._onDragEnd = this.onDragEnd.bind(this);
        this._onWheel = this.onWheel.bind(this);

        this.scrollableElement = null;
        this.thumb = null;
        this.isDragging = false;
        this.resizeObserver = null;
      }

      connectedCallback() {
        // 查找可滚动容器：[data-custom-scrollbar-container]
        this.scrollableElement = this.parentElement?.matches('[data-custom-scrollbar-container]') ? this.parentElement : this.parentElement?.querySelector('[data-custom-scrollbar-container]');

        if (!this.scrollableElement) {
          console.warn('CustomScrollBar: 未找到 [data-custom-scrollbar-container] 滚动容器');
          return;
        }

        this.thumb = this.querySelector('[data-custom-scrollbar-thumb]');
        if (!this.thumb) {
          console.warn('CustomScrollBar: 未找到 [data-custom-scrollbar-thumb] 元素');
          return;
        }

        // 初始化显示状态和滑块
        this.updateScrollbarVisibility();
        this.updateThumb();

        // 监听滚动事件
        this.scrollableElement.addEventListener('scroll', this._onScroll);

        // 监听窗口大小变化
        window.addEventListener('resize', this._onResize);

        // 使用 ResizeObserver 监听内容高度变化
        if (window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(() => {
            this.updateScrollbarVisibility();
            this.updateThumb();
          });
          this.resizeObserver.observe(this.scrollableElement);
          // 同时监听内部内容容器，确保动态内容变化时能响应
          const innerContainer = this.scrollableElement.firstElementChild;
          if (innerContainer) {
            this.resizeObserver.observe(innerContainer);
          }
        }

        // 监听拖拽事件
        this.thumb.addEventListener('mousedown', this._startDrag);

        // 让滚动条区域支持滚轮滚动
        this.addEventListener('wheel', this._onWheel, { passive: false });
      }

      disconnectedCallback() {
        if (this.scrollableElement) {
          this.scrollableElement.removeEventListener('scroll', this._onScroll);
        }
        window.removeEventListener('resize', this._onResize);

        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }

        if (this.thumb) {
          this.thumb.removeEventListener('mousedown', this._startDrag);
        }

        this.removeEventListener('wheel', this._onWheel);

        // 清理全局拖拽事件
        document.removeEventListener('mousemove', this._onDragMove);
        document.removeEventListener('mouseup', this._onDragEnd);
      }

      /**
       * 判断容器是否可以滚动
       * @returns {boolean}
       */
      isScrollable() {
        if (!this.scrollableElement) return false;
        const clientHeight = this.scrollableElement.clientHeight;
        const scrollHeight = this.scrollableElement.scrollHeight;
        // 内容高度大于可视高度，且差值大于0.5px（避免浮点误差）
        return scrollHeight - clientHeight > 0.5;
      }

      /**
       * 更新滚动条可见性
       * 可滚动时显示（常态可见），不可滚动时隐藏
       */
      updateScrollbarVisibility() {
        const scrollable = this.isScrollable();

        if (scrollable) {
          // 可滚动：显示滚动条
          this.style.opacity = '1';
          this.style.pointerEvents = 'auto';
          this.classList.remove('custom-scrollbar--hidden');
        } else {
          // 不可滚动：隐藏滚动条
          this.style.opacity = '0';
          this.style.pointerEvents = 'none';
          this.classList.add('custom-scrollbar--hidden');
        }
      }

      /**
       * 更新滑块的高度和位置
       */
      updateThumb() {
        if (!this.scrollableElement || !this.thumb) return;

        const clientHeight = this.scrollableElement.clientHeight;
        const scrollHeight = this.scrollableElement.scrollHeight;

        // 无法滚动时隐藏滑块（高度设为0）
        if (scrollHeight <= clientHeight) {
          this.thumb.style.height = '0px';
          this.thumb.style.top = '0px';
          this.classList.add('custom-scrollbar--hidden');
          return;
        }
        this.classList.remove('custom-scrollbar--hidden');

        // 获取滚动条轨道高度（滚动条自身高度）
        const trackHeight = this.clientHeight;

        // 计算滑块高度 = (可视区高度 / 内容高度) * 轨道高度
        let thumbHeight = (clientHeight / scrollHeight) * trackHeight;

        // 设置最小高度 32px，确保可拖拽
        const minThumbHeight = 32;
        if (thumbHeight < minThumbHeight) {
          thumbHeight = minThumbHeight;
        }

        // 滑块高度不能超过轨道高度
        if (thumbHeight > trackHeight) {
          thumbHeight = trackHeight;
        }

        this.thumb.style.height = `${thumbHeight}px`;

        // 计算滑块位置
        const maxScrollTop = scrollHeight - clientHeight;
        const currentScrollTop = this.scrollableElement.scrollTop;
        const maxThumbTop = trackHeight - thumbHeight;

        let thumbTop = (currentScrollTop / maxScrollTop) * maxThumbTop;
        if (isNaN(thumbTop)) thumbTop = 0;
        thumbTop = Math.min(maxThumbTop, Math.max(0, thumbTop));

        this.thumb.style.top = `${thumbTop}px`;
      }

      /**
       * 滚动事件回调
       */
      onScroll(e) {
        this.updateThumb();
        // 确保可见性正确（滚动过程中如果内容变化，重新检查）
        if (this.isScrollable() && this.style.opacity === '0') {
          this.updateScrollbarVisibility();
        }
      }

      /**
       * 窗口大小变化回调
       */
      onResize() {
        this.updateScrollbarVisibility();
        this.updateThumb();
      }

      /**
       * 开始拖拽滚动条
       * @param {MouseEvent} e
       */
      startDrag(e) {
        e.preventDefault();
        if (!this.isScrollable()) return;

        this.isDragging = true;

        // 记录起始鼠标位置和滑块起始位置
        this.startMouseY = e.clientY;
        const thumbTop = parseFloat(this.thumb.style.top);
        this.startThumbTop = isNaN(thumbTop) ? 0 : thumbTop;

        // 记录轨道和滑块尺寸
        this.trackHeight = this.clientHeight;
        this.thumbHeight = this.thumb.clientHeight;

        // 添加全局事件监听
        document.addEventListener('mousemove', this._onDragMove);
        document.addEventListener('mouseup', this._onDragEnd);

        // 防止拖拽时选中文本
        document.body.style.userSelect = 'none';
        this.thumb.style.cursor = 'grabbing';
      }

      /**
       * 拖拽移动中
       * @param {MouseEvent} e
       */
      onDragMove(e) {
        if (!this.isDragging) return;
        e.preventDefault();

        const deltaY = e.clientY - this.startMouseY;
        let newThumbTop = this.startThumbTop + deltaY;
        const maxThumbTop = this.trackHeight - this.thumbHeight;

        // 限制滑块移动范围
        newThumbTop = Math.min(maxThumbTop, Math.max(0, newThumbTop));

        // 根据滑块位置计算滚动比例
        const scrollRatio = maxThumbTop > 0 ? newThumbTop / maxThumbTop : 0;
        const maxScrollTop = this.scrollableElement.scrollHeight - this.scrollableElement.clientHeight;
        const targetScrollTop = scrollRatio * maxScrollTop;

        // 设置滚动位置
        this.scrollableElement.scrollTop = targetScrollTop;

        // 手动更新滑块位置（滚动事件也会触发，但这里立即更新更流畅）
        this.updateThumb();
      }

      /**
       * 拖拽结束
       */
      onDragEnd() {
        this.isDragging = false;
        document.removeEventListener('mousemove', this._onDragMove);
        document.removeEventListener('mouseup', this._onDragEnd);
        document.body.style.userSelect = '';
        if (this.thumb) {
          this.thumb.style.cursor = '';
        }
      }

      /**
       * 滚动条区域滚轮滚动
       * @param {WheelEvent} e
       */
      onWheel(e) {
        if (!this.scrollableElement || !this.isScrollable()) return;

        e.preventDefault();

        // 将滚轮增量应用到滚动容器
        const delta = e.deltaY || e.deltaX || e.detail;
        this.scrollableElement.scrollTop += delta;

        // 滚动事件会自动触发 updateThumb，无需额外处理
      }
    });
}